import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bars } from "react-loader-spinner";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../redux/hooks";
import { setUser } from "../../redux/reducers/userReducer";
import skillsService from "../../services/skillsService";
import UserService from "../../services/userService";
import CloseButton from "../../shared/components/CloseButton";
import PPTextInput from "../../shared/components/PPTextInput";
import PrimaryButton from "../../shared/components/PrimaryButton";
import { isIOS } from "../../shared/utils/PlatformUtils";
import { AppColors } from "../../theme/AppTheme";
import SkillsArray from "./SkillsArray";
import TitlesSearch from "./TitlesSearch";
import { DefaultModel } from "../../shared/models/LLMModels";

const StyledContainer = styled('div')((props: { isMobile: boolean }) => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: props.isMobile ? '100%' : '70%',
    maxWidth: props.isMobile ? '100%' : '650px',
    minHeight: props.isMobile ? '100%' : '70vh',
    maxHeight: props.isMobile ? '100%' : '70vh',
    backgroundColor: AppColors.white,
    padding: props.isMobile ? '0px 20px' : undefined,
}));

const StyledScrollingContainer = styled('div')((props: { isMobile: boolean }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: props.isMobile ? 'calc(100%)' : '100%',
    overflowY: 'auto',
    backgroundColor: AppColors.white,
    padding: props.isMobile ? '0px 24px' : '0px 80px 64px',
    paddingBottom: '64px',
    flexGrow: 1,
}));

const StyledButtons = styled('div')((props: { isMobile: boolean }) => ({
    padding: '12px 75px',
    borderTop: `1px solid ${AppColors.grey.border}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '22px',
    width: props.isMobile ? 'calc(100%)' : '100%',
}));

interface TitlesScreenProps {
    hideCloseButton?: boolean;
    onNext?: () => void;
    onClose: () => void;
    onboarding?: boolean;
}

const TitlesScreen = (props: TitlesScreenProps) => {
    const { onboarding, onNext, onClose } = props;
    const { t } = useTranslation();
    const isMobile = useAppSelector(store => store.appState.isMobile);
    const user = useAppSelector(store => store.userState.currentUser);

    const [showSearch, setShowSearch] = useState(false);
    const [titles, setTitles] = useState<string[]>(user?.info?.desiredPositions ?? []);
    const [recommendedTitles, setRecommendedTitles] = useState<string[]>([]);
    const [topBarFull, setTopBarFull] = useState(true);

    // Skill Search
    const [searchText, setSearchText] = useState('');
    const [searchFillColor, setSearchFillColor] = useState(AppColors.grey.lightest);
    const [searchPlaceholderColor, setSearchPlaceholderColor] = useState(AppColors.grey.dark);
    const [searchBorderColor, setSearchBorderColor] = useState(AppColors.white);

    const [recommendedSkillsLoading, setRecommendedSkillsLoading] = useState(true);

    const [saving, setSaving] = useState(false);

    const anchorElement = useRef<HTMLDivElement>(null);
    const scrollContainer = useRef<HTMLDivElement>(null);
    const innerScrollContainer = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();

    useEffect(() => {
        setTitles(user?.info?.desiredPositions ?? []);
    }, [user, user?.info, user?.info?.desiredPositions]);

    useEffect(() => {
        const fetchRecommendedSkills = async () => {
            try {
                const recos = await skillsService.getRecommendedSkillsAndPositions(DefaultModel);
                setRecommendedTitles(recos?.positions ?? []);
            } catch (e) {
                console.error(e);
            } finally {
                setRecommendedSkillsLoading(false);
            }
        }
        fetchRecommendedSkills();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const isIos = isIOS();
            if (innerScrollContainer.current) {
                const scrollTop = isIos
                    ? -(document.getElementById('innerContainer')?.getBoundingClientRect().top ?? 0)
                    : innerScrollContainer.current.scrollTop;

                // truncate the decimal
                const roundedScrollTop = Math.round(scrollTop);
                if (!topBarFull && roundedScrollTop < 45) {
                    setTopBarFull(true);
                } else if (topBarFull && roundedScrollTop >= 53) {
                    setTopBarFull(false);
                }
            }
        };

        if (innerScrollContainer.current) {
            innerScrollContainer.current.addEventListener('scroll', handleScroll);
            innerScrollContainer.current.addEventListener('touchmove', handleScroll);
        }

        return () => {
            if (innerScrollContainer.current) {
                innerScrollContainer.current.removeEventListener('scroll', handleScroll);
                innerScrollContainer.current.removeEventListener('touchmove', handleScroll);
            }
        };
    }, [innerScrollContainer, topBarFull]);

    const onSave = async () => {
        setSaving(true);
        try {
            const user = await UserService.updateUserInfo({ desiredPositions: titles });
            dispatch(setUser(user));
            if (onboarding) {
                onNext?.();
            } else {
                onClose()
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const onTitleAdded = (skill: string) => {
        setRecommendedTitles(recommendedTitles.filter(s => s !== skill));
        setTitles([...titles, skill]);
    }

    const onTitleRemoved = (skill: string) => {
        setTitles(titles.filter(s => s !== skill));
    }

    const onSearchFocused = () => {
        setSearchFillColor(AppColors.white);
        setSearchPlaceholderColor(AppColors.grey.darkish);
        setSearchBorderColor(AppColors.grey.border);
        setShowSearch(true);

    }

    const onSearchBlurred = () => {
        setSearchFillColor(AppColors.grey.lightest);
        setSearchPlaceholderColor(AppColors.grey.dark);
        setSearchBorderColor(AppColors.white);
        setShowSearch(false);
    }

    const onSearchCompleted = () => {
        setSearchText('');
        setShowSearch(false);
        onSearchBlurred();
    }

    const suggestionSubtitle = user?.resume?.textResume ? 'Below are desired positions you may want to add to your profile.' : 'Upload your resume to see suggested positions.';

    return (
        <StyledContainer isMobile={isMobile} ref={scrollContainer}>
            <CloseButton onClick={onClose} back={onboarding} />
            <StyledScrollingContainer ref={innerScrollContainer} isMobile={isMobile}>
                {showSearch &&
                    <TitlesSearch
                        searchText={searchText}
                        onSelectTitle={onTitleAdded}
                        onRemoveTitle={onTitleRemoved}
                        selectedTitles={titles}
                        anchorElement={anchorElement.current}
                        scrollContainer={innerScrollContainer.current}
                        onDone={onSearchCompleted}
                    />}
                <span style={{ minHeight: '54px' }} />
                <Typography variant='h2' style={{ transition: 'all 0.2s ease-in-out', fontSize: topBarFull ? '21px' : '16px', zIndex: 9999999, marginBottom: '9px', position: 'sticky', top: 0, width: '100%', textAlign: 'center', padding: "12px 0px", background: "white" }}>
                    {`${onboarding ? 'Add' : 'Edit'} Desired Positions`}
                </Typography>
                <Typography variant='h5' style={{
                    textAlign: 'center',
                    marginBottom: '40px',
                    color: AppColors.grey.darkish
                }}>
                    {onboarding ? 'Add one or more positions below. These will be used to help match you to relevant jobs' : 'Add and manage your desired positions below.'}
                </Typography>

                <Typography variant="body2" style={{ width: '100%', textAlign: 'left', marginBottom: '16px' }}>
                    Your Desired Positions
                </Typography>
                <PPTextInput id={'position-search'} reference={anchorElement}
                    value={searchText}
                    onValueChanged={(newValue) => setSearchText(newValue)}
                    bottomPadding="26px"
                    placeholder="Enter desired position..."
                    placeholderColor={searchPlaceholderColor}
                    fillColor={searchFillColor}
                    borderColor={searchBorderColor}
                    onFocused={onSearchFocused}
                    // onBlurred={onSearchBlurred}
                    zIndex={1000}
                />
                <SkillsArray skills={titles} onRemove={onTitleRemoved} allSelected={true} emptyStateText="" />
                <span style={{ minHeight: '40px' }} />
                <SkillsArray noExpand title={'Suggested Positions'} subtitle={suggestionSubtitle} skills={recommendedTitles} onSelected={onTitleAdded} allSelected={false} emptyStateText="" loading={recommendedSkillsLoading} />
            </StyledScrollingContainer>
            <StyledButtons isMobile={isMobile}>
                {/* <OutlinedButton title={(onboarding ? 'back' : t(Strings.cancel)).toUpperCase()} height={'48px'} disabled={false} onClick={onClose} /> */}
                <PrimaryButton title={onboarding ? 'NEXT' : 'SAVE'} height={'48px'} disabled={titles.length === 0} paddingHorizontal="20%" onClick={onSave} />
            </StyledButtons>
            {saving && <div style={{ zIndex: 999999, background: 'rgba(0,0,0,0.3)', position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bars
                    height="30"
                    width="30"
                    color={AppColors.pink.dark}
                    ariaLabel="bars-loading"
                    visible={true}
                />
            </div>}
        </StyledContainer>
    )
}

export default TitlesScreen